import { ListItem, ListItemButton, ListItemText, Typography, alpha, styled } from '@mui/material';
import clsx from 'clsx';
import Image from 'next/image';
import { useMemo } from 'react';

const ROW_HEIGHT = 52;
const LOGO_SIZE = 36;

const StyledListItem = styled(ListItem)(({ theme: { palette } }) => ({
	'&.error': {
		backgroundColor: alpha(palette.error.main, 0.2),
	},
}));

const StyledListItemButton = styled(ListItemButton)(({ theme: { spacing } }) => ({
	height: ROW_HEIGHT,
	padding: spacing(1),
	gap: spacing(2),
}));

const ExpandedWidgetCustomAvatar = styled(Typography)(({ theme: { palette, shape } }) => ({
	height: LOGO_SIZE,
	width: LOGO_SIZE,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	border: `2px solid ${palette.primary.main}`,
	borderRadius: shape.borderRadius,
	color: palette.primary.main,
}));

const StyledImage = styled(Image, {
	shouldForwardProp: (prop) => prop !== 'disabled',
})<{ disabled: boolean }>(({ disabled, theme: { shape } }) => ({
	borderRadius: shape.borderRadius,
	...(disabled && { filter: 'grayscale(1)' }),
}));

type GenericExternalApplicationProps = {
	imageSrc?: string;
	disabled?: boolean;
	disabledMessage?: string;
	errorMessage?: string;
	name: string;
};

const ExternalApplicationListItem = ({
	imageSrc,
	disabled = false,
	disabledMessage,
	errorMessage,
	name,
}: GenericExternalApplicationProps) => {
	const listItemTextSecondary = useMemo(() => {
		if (errorMessage) {
			return errorMessage;
		}

		if (disabled) {
			return disabledMessage || 'Indisponible sur mobile';
		}

		return undefined;
	}, [disabled, disabledMessage, errorMessage]);

	return (
		<StyledListItem disablePadding className={clsx({ error: errorMessage })}>
			<StyledListItemButton disabled={disabled || !!errorMessage}>
				{imageSrc ? (
					<StyledImage
						disabled={disabled}
						src={imageSrc}
						alt={`Logo ${name}`}
						width={36}
						height={36}
						objectFit="contain"
					/>
				) : (
					<ExpandedWidgetCustomAvatar variant="h6">{name.charAt(0)}</ExpandedWidgetCustomAvatar>
				)}

				<ListItemText primary={name} secondary={listItemTextSecondary} />
			</StyledListItemButton>
		</StyledListItem>
	);
};

export default ExternalApplicationListItem;
